import { HIRING_RELIABILITY_CONFIG } from "@clipboard-health/contract-backend-main";
import { Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import {
  BottomSheet,
  Illustration,
  Text,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { TextButton } from "@src/appV2/redesign/system/ui-components";
import { capitalize } from "lodash";
import { Link } from "react-router-dom";

import {
  INTERVIEW_TYPE,
  InterviewStatus,
  type InterviewType,
} from "../../WorkerShifts/api/useFetchPaginatedInterviews";
import { useReportInterviewLateness } from "../api/useReportInterviewLateness";
import { useUpdateInterview } from "../api/useUpdateInterview";

interface PastInterviewCancelBottomSheetProps {
  isProspectWorkplace: boolean;
  modalState: UseModalState;
  interviewId: string;
  startsInWorkplaceToday?: boolean;
  attendanceScore: number;
  interviewType: InterviewType;
  onCancelInterview: () => Promise<void>;
}

export function PastInterviewCancelBottomSheet(props: PastInterviewCancelBottomSheetProps) {
  const {
    isProspectWorkplace,
    modalState,
    interviewId,
    onCancelInterview,
    startsInWorkplaceToday,
    attendanceScore,
    interviewType,
  } = props;
  const { showErrorToast, showSuccessToast } = useToast();

  const isPhoneScreen = interviewType === INTERVIEW_TYPE.PHONE_SCREEN;
  const cancelLabel = isPhoneScreen ? "phone call" : "interview";
  const hasAttendanceCredits = attendanceScore > 0;
  const restrictedDays = HIRING_RELIABILITY_CONFIG.RESTRICTED_DAYS;

  const getStartedInterviewDescription = () => {
    if (isPhoneScreen) {
      return `Your ${cancelLabel} has already started. Are you sure you want to cancel?`;
    }

    if (hasAttendanceCredits) {
      return `Please start heading to the facility now. Cancelling or no-show is a violation of the attendance policy. However, since you have a strong interview attendance record — we're going to give you a free pass on this one. Your account will not be restricted. If you cancel or no-show in the future, you'll be restricted from viewing jobs for ${restrictedDays} days.`;
    }

    return `Please start heading to the facility now. If you cancel or no-show, you'll be restricted from viewing job openings for ${restrictedDays} days.`;
  };

  const getPastInterviewDescription = () => {
    if (isPhoneScreen) {
      return undefined;
    }

    if (hasAttendanceCredits) {
      return `Cancelling your past interview is a violation of the attendance policy. However, since you have a strong interview attendance record — we're going to give you a free pass on this one. Your account will not be restricted. If you cancel your past interview in the future, you'll be restricted from viewing jobs for ${restrictedDays} days.`;
    }

    return `If you cancel your past interview, you'll be restricted from viewing job openings for ${restrictedDays} days.`;
  };

  const eventContext = {
    interviewId,
    isProspectWorkplace,
    attendanceScore,
  };
  useLogEffect(APP_V2_APP_EVENTS.PLACEMENT_PAST_INTERVIEW_CANCEL_MODAL_VIEWED, eventContext);

  const { mutateAsync: cancelInterview, isLoading: isCancelling } = useUpdateInterview({
    onSuccess: async () => {
      showSuccessToast(`${capitalize(cancelLabel)} cancelled`);
      modalState.closeModal();
      await onCancelInterview();
    },
    onError: () => {
      showErrorToast(`An error occurred cancelling ${cancelLabel}`);
    },
  });

  const { mutateAsync: reportLateness, isLoading: isReportingLateness } =
    useReportInterviewLateness({
      onSuccess: () => {
        showSuccessToast(
          "Thanks for letting us know! We gave the facility a heads up. They're looking forward to seeing you soon!"
        );
        modalState.closeModal();
      },
      onError: () => {
        showErrorToast(`An error occurred reporting lateness for ${cancelLabel}`);
      },
    });

  const handleCancel = async () => {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_PAST_INTERVIEW_CANCEL_MODAL_ACTIONS, {
      ...eventContext,
      action: "cancel",
    });

    await cancelInterview({
      interviewId,
      attributes: {
        status: InterviewStatus.CANCELLED,
      },
    });
  };

  const handleReportLateness = async () => {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_PAST_INTERVIEW_CANCEL_MODAL_ACTIONS, {
      ...eventContext,
      action: "report-lateness",
    });

    await reportLateness({
      interviewId,
    });
  };

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="vertical">
          {startsInWorkplaceToday ? (
            <>
              <LoadingButton
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 1, mb: 3 }}
                isLoading={isReportingLateness}
                onClick={handleReportLateness}
              >
                I&apos;m just running late
              </LoadingButton>
              <TextButton
                LinkComponent={Link}
                size="large"
                disabled={isCancelling}
                onClick={handleCancel}
              >
                I need to cancel the {isPhoneScreen ? "phone call" : "interview"}
              </TextButton>
            </>
          ) : (
            <LoadingButton
              fullWidth
              variant="destructive"
              size="large"
              sx={{ mt: 1, mb: 3 }}
              isLoading={isCancelling}
              onClick={handleCancel}
            >
              Cancel
            </LoadingButton>
          )}
        </DialogFooter>
      }
    >
      <IconButton
        size="small"
        iconType="close"
        variant="outlined"
        sx={{ position: "absolute", top: 24, right: 16 }}
        onClick={modalState.closeModal}
      />
      <Stack gap={7} px={7} pb={8} pt="76px" alignItems="center" justifyContent="center">
        {startsInWorkplaceToday ? (
          <>
            <Illustration type="calendar-warning" />
            <Text variant="h2" textAlign="center">
              Your {cancelLabel} has started
            </Text>
            <Text textAlign="center" color={(theme) => theme.palette.text.secondary}>
              {getStartedInterviewDescription()}
            </Text>
          </>
        ) : (
          <>
            <Illustration type="cancellation" />
            <Text variant="h2" textAlign="center">
              Cancel your {cancelLabel}?
            </Text>
            <Text textAlign="center" color={(theme) => theme.palette.text.secondary}>
              {getPastInterviewDescription()}
            </Text>
          </>
        )}
      </Stack>
    </BottomSheet>
  );
}
